import { MutationOptions, useMutation, useQuery, UseQueryOptions } from "@tanstack/react-query";
import { fetchItem, fetchItemSummary, fetchItemWithRelationships } from "../utilities/dataSourceAdapters";
import { Entity } from "../types";
import { apiFetch, apiFetchNoResponseRequired } from "../utilities/api";
import { REACT_APP_API_ROOT } from "../services";
import { removeTrailingSlash } from "../utilities/common";

const apiBaseURL = removeTrailingSlash(REACT_APP_API_ROOT);

export const getEntityQueryKey = (entityId: string | number, selectedEndpoint: any, level: "L1" | "L2" | "L3") => [
  "entity",
  String(entityId),
  selectedEndpoint?.tableName || selectedEndpoint,
  level
];

export const useEntityFetch = (
  {
    entityId,
    selectedEndpoint,
    userProfile,
    allEndpoints,
    level,
    onProgress
  }: {
    entityId: string;
    selectedEndpoint: any;
    userProfile: any;
    allEndpoints?: any;
    level: "L1" | "L2" | "L3";
    onProgress?: (step: string, current: number, total: number) => void;
  },
  options?: Omit<UseQueryOptions<Entity, Error>, "queryKey" | "queryFn">
) =>
  useQuery({
    queryKey: getEntityQueryKey(entityId, selectedEndpoint, level),
    queryFn: () => {
      if (level === "L1") return fetchItemSummary(entityId, selectedEndpoint, userProfile);
      if (level === "L2") return fetchItem(entityId, selectedEndpoint, userProfile, allEndpoints);
      return fetchItemWithRelationships(entityId, selectedEndpoint, userProfile, allEndpoints, onProgress);
    },
    ...options
  });

/**
 * API hook to delete Entity
 * */
export function useDeleteEntityMutation(
  options?: Omit<MutationOptions<unknown, Error, { id: string | number; apiEndpointUrl: string }>, "mutationFn">
) {
  return useMutation({
    ...options,
    mutationFn(payload) {
      const apiEndpointUrl = removeTrailingSlash(payload?.apiEndpointUrl);
      return apiFetchNoResponseRequired(`${apiBaseURL}/${apiEndpointUrl}/${payload?.id}/`, {
        method: "DELETE"
      });
    }
  });
}

/**
 * API hook to delete Entity's media
 * */
export function useDeleteEntityMediaMutation(options?: Omit<MutationOptions<unknown, Error, { id: string | number }>, "mutationFn">) {
  return useMutation({
    ...options,
    mutationFn(payload) {
      return apiFetchNoResponseRequired(`${apiBaseURL}/api/user/media/${payload?.id}/`, {
        method: "DELETE"
      });
    }
  });
}

/**
 * API hook to unlink media from Entity
 * */
export function useEntityMediaUnlinkMutation(
  options?: Omit<MutationOptions<unknown, Error, { id: string | number; data: any }>, "mutationFn">
) {
  return useMutation({
    ...options,
    mutationFn(payload) {
      return apiFetch(`${apiBaseURL}/api/user/media/${payload?.id}/`, {
        method: "PATCH",
        body: payload?.data
      });
    }
  });
}
